import { useState, useEffect } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ArrowLeftSquare } from "bootstrap-icons/icons/arrow-left-square-fill.svg";
import Spinner from "../../spinner/Spinner";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import formatDate from "../../../components/utils/DateFormatter";

const RemittanceDetails = () => {
    const [remittanceDetails, setRemittanceDetails] = useState({});
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { data } = location.state || {};

    useEffect(() => {
        if (data) {
            // Safely map data
            const mappedData = {
                remittance_number: data.remittance_number || "NOT AVAILABLE",
                type_label: data.type_label || "NOT AVAILABLE",
                amount: data.amount || "0.00",
                description: data.description || "NOT AVAILABLE",
                branch_note: data.branch_note || "NOT AVAILABLE",
                status: data.status || "NOT AVAILABLE",
                rejection_reason: data.rejection_reason || null,
                reference: data.reference || "NOT AVAILABLE",
                submitted_at: data.submitted_at || null,
                approved_at: data.approved_at || null,
                completed_at: data.completed_at || null,
                created_at: data.created_at || null,
                time_ago: data.time_ago || "NOT AVAILABLE",
                branch: data.branch || {},
                initiator: data.initiator || {},
                approver: data.approver || {},
            };
            setRemittanceDetails(mappedData);
        }
    }, [data]);

    const Card = ({ title, children }) => (
        <div className="bg-white shadow-md rounded-2xl p-5 border border-gray-100">
            <h2 className="text-lg font-semibold text-darkGreen3 mb-4 border-b pb-2">
                {title}
            </h2>
            <div className="space-y-2">{children}</div>
        </div>
    );

    const InfoRow = ({ label, value, badge, highlight }) => (
        <div className="flex justify-between items-center py-1">
            <span className="text-gray-500 font-medium">{label}</span>
            {badge ? (
                <span
                    className={`px-2 py-1 text-xs rounded-full ${value === "completed" || value === "approved"
                        ? "bg-green-600 text-white"
                        : value === "pending"
                            ? "bg-orange1 text-white"
                            : "bg-red-600 text-white"
                        }`}
                >
                    {value?.toUpperCase()}
                </span>
            ) : (
                <span
                    className={`text-gray-800 ${highlight ? "font-semibold text-darkGreen3" : ""
                        }`}
                >
                    {value || "-"}
                </span>
            )}
        </div>
    );

    return (
        <MainLayouts>
            <div className="relative sm:rounded-lg py-6 w-full">
                {/* Breadcrumb */}
                <section className="w-full pr-[3%] pt-3 my-3 bg-gray-100">
                    <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                        <ol className="list-none p-0 inline-flex mb-2">
                            <li className="flex items-center">
                                <p className="text-darkGreen3 pr-2">Operations Remittance</p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-orange1">Details</p>
                            </li>
                        </ol>
                    </nav>
                </section>

                {/* Content */}
                {loading ? (
                    <Spinner loading={loading} />
                ) : (
                    <section className="text-gray-600 body-font">
                        <div className="container">
                            {/* Back button */}
                            <div className="flex justify-end mb-3">
                                <Link
                                    to="/operations/branch/remittance"
                                    className="w-full sm:w-auto text-dGrey3 bg-darkGreen3 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                >
                                    <span className="flex justify-center sm:justify-start mx-2">
                                        <ArrowLeftSquare className="mr-2" />
                                        Back
                                    </span>
                                </Link>
                            </div>

                            <section className="text-gray-600 body-font px-4 lg:px-8 pb-10">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {/* LEFT SIDE */}
                                    <div className="lg:col-span-2 space-y-6">
                                        {/* General Info */}
                                        <Card title="General Information">
                                            <InfoRow
                                                label="Remittance Number"
                                                value={remittanceDetails.remittance_number}
                                            />
                                            <InfoRow
                                                label="Type"
                                                value={remittanceDetails.type_label}
                                            />
                                            <InfoRow
                                                label="Amount"
                                                value={CurrencyFormatter(remittanceDetails.amount)}
                                                highlight
                                            />
                                            <InfoRow
                                                label="Status"
                                                value={remittanceDetails.status}
                                                badge
                                            />
                                            <InfoRow
                                                label="Description"
                                                value={remittanceDetails.description}
                                            />
                                            <InfoRow
                                                label="Branch Note"
                                                value={remittanceDetails.branch_note}
                                            />
                                            {remittanceDetails.rejection_reason && (
                                                <InfoRow
                                                    label="Rejection Reason"
                                                    value={remittanceDetails.rejection_reason}
                                                />
                                            )}
                                            <InfoRow
                                                label="Reference"
                                                value={remittanceDetails.reference}
                                            />
                                        </Card>

                                        {/* Dates */}
                                        <Card title="Submission Information">
                                            <InfoRow
                                                label="Date Submitted"
                                                value={formatDate(remittanceDetails.submitted_at)}
                                            />
                                            <InfoRow
                                                label="Date Approved "
                                                value={formatDate(remittanceDetails.approved_at)}
                                            />
                                            <InfoRow
                                                label="Date Completed "
                                                value={formatDate(remittanceDetails.completed_at)}
                                            />
                                            <InfoRow
                                                label="Date Created"
                                                value={formatDate(remittanceDetails.created_at)}
                                            />
                                            {/* <InfoRow
                                                label="Time Ago"
                                                value={remittanceDetails.time_ago}
                                            /> */}
                                        </Card>
                                    </div>

                                    {/* RIGHT SIDE */}
                                    <div className="lg:col-span-1 space-y-6">
                                        <Card title="Branch Information">
                                            <InfoRow
                                                label="Name"
                                                value={remittanceDetails.branch?.name}
                                            />
                                            <InfoRow
                                                label="Email"
                                                value={remittanceDetails.branch?.email}
                                            />
                                            <InfoRow
                                                label="Phone"
                                                value={remittanceDetails.branch?.phone_number}
                                            />
                                        </Card>

                                        {/* <Card title="Initiator Information">
                                            <InfoRow
                                                label="Full Name"
                                                value={remittanceDetails.initiator?.fullname}
                                            />
                                            <InfoRow
                                                label="Email"
                                                value={remittanceDetails.initiator?.email}
                                            />
                                        </Card> */}

                                        {/* <Card title="Approver Information">
                                            <InfoRow
                                                label="Full Name"
                                                value={remittanceDetails.approver?.fullname}
                                            />
                                            <InfoRow
                                                label="Email"
                                                value={remittanceDetails.approver?.email}
                                            />
                                        </Card> */}
                                    </div>
                                </div>
                            </section>
                        </div>
                    </section>
                )}
            </div>
        </MainLayouts>
    );
};

export default RemittanceDetails;
